/**
 * DEV-1464 / POLICY_COMMUNITY_DELETION.md — client for the community lifecycle
 * endpoints (dissolve / reactivate / soft-delete / restore / trash / purge).
 *
 * The server is the sole authority on what the caller may do. Never re-derive
 * the guards here: fetch `getCommunityLifecycle` and render exactly what it
 * says. If `canDelete` is false, disable the control and show the reason
 * (CLAUDE.md, gated affordances disable-and-explain — never enabled-then-fail).
 */

import { apiFetch } from './client';

export interface CommunityContentCounts {
  chronicles: number;
  miniProjects: number;
  playbooks: number;
  workspaces: number;
  newsItems: number;
  total: number;
}

/**
 * Why a lifecycle action is (un)available. Computed server-side; the UI binds
 * to it directly.
 */
export interface CommunityLifecycleState {
  entityGuid: string;
  entityType: string;
  name: string;

  dissolvedAt: string | null;
  deletedAt: string | null;
  state: 'active' | 'dissolved' | 'deleted';
  purgeScheduledAt: string | null;

  /** §2 structural guards — integrity constraints; they bind sysadmins too. */
  hasChildren: boolean;
  childCount: number;
  isGuardian: boolean;
  guardedCount: number;

  /** §5 — content is never collateral damage. Any content ⇒ delete is refused. */
  contentCounts: CommunityContentCounts;
  hasContent: boolean;

  callerCanDissolve: boolean;
  callerCanDelete: boolean;
  callerIsGuardian: boolean;

  canDissolve: boolean;
  canReactivate: boolean;
  canDelete: boolean;

  /**
   * Machine-readable, in priority order. Map to localized copy:
   *   not_authorized · geo_sysadmin_only · has_children · is_guardian ·
   *   has_content · already_deleted
   */
  blockReasons: string[];
}

export interface DeletedCommunity {
  entityGuid: string;
  name: string;
  entityType: string;
  deletedAt: string;
  deletedByUserGuid: string | null;
  deletedByDisplayName: string | null;
  purgeScheduledAt: string;
  daysRemaining: number;
}

const BASE = '/api/communitymetadata/entities';

export function getCommunityLifecycle(guid: string): Promise<CommunityLifecycleState> {
  return apiFetch<CommunityLifecycleState>(`${BASE}/${guid}/lifecycle`);
}

/** Reversible — no type-to-confirm gate (policy §6). */
export function dissolveCommunity(guid: string, reason?: string): Promise<CommunityLifecycleState> {
  return apiFetch<CommunityLifecycleState>(`${BASE}/${guid}/dissolve`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason ?? null }),
  });
}

export function reactivateCommunity(guid: string, reason?: string): Promise<CommunityLifecycleState> {
  return apiFetch<CommunityLifecycleState>(`${BASE}/${guid}/reactivate`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason ?? null }),
  });
}

/**
 * Move to trash. The 30-day window IS the protection, so no type-to-confirm
 * (policy §6). Throws on 409 when a guard fires — the caller should have
 * disabled the control already, but the server refuses regardless.
 */
export function softDeleteCommunity(guid: string, reason?: string): Promise<CommunityLifecycleState> {
  return apiFetch<CommunityLifecycleState>(`${BASE}/${guid}`, {
    method: 'DELETE',
    body: JSON.stringify({ reason: reason ?? null }),
  });
}

/** Restore undoes the DELETION, not the dissolution — a community dissolved before deletion comes back dissolved. */
export function restoreCommunity(guid: string, reason?: string): Promise<CommunityLifecycleState> {
  return apiFetch<CommunityLifecycleState>(`${BASE}/${guid}/restore`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason ?? null }),
  });
}

export function getDeletedCommunities(): Promise<DeletedCommunity[]> {
  return apiFetch<DeletedCommunity[]>(`${BASE}/deleted`);
}

/**
 * Empty Trash — the one strong gate: the caller must echo the community name,
 * which the server re-checks rather than trusting this dialog.
 *
 * CURRENTLY GATED SERVER-SIDE (501) pending architect ratification of the §4
 * cascade table. Callers must handle that: the community stays in the trash and
 * remains fully restorable.
 */
export function purgeCommunity(
  guid: string,
  confirmName: string,
  reason?: string,
): Promise<void> {
  return apiFetch<void>(`${BASE}/${guid}/purge`, {
    method: 'DELETE',
    body: JSON.stringify({ confirmName, reason: reason ?? null }),
  });
}
